import {
  cancelElevenLabsPlayback,
  cancelSpeechPlayback,
  previewVoiceAudio,
} from './speechPlayback';

export const ELEVENLABS_TTS_MODEL = 'eleven_v3';

export const LANGUAGE_OPTIONS = ['English (Aust)', 'English (Global)', 'Spanish', 'Afrikaans'] as const;
export const GENDER_OPTIONS = ['Female', 'Male'] as const;
export const WEATHER_OPTIONS = ['°C', '°F'] as const;

export const LEGACY_HARRY_VOICE_ID = 'mHlts5QHKpF8NPCOjcgW';
export const TONY_VOICE_ID = 'v9I7auPeR1xGKYRPwQGG';

export type LanguageOption = (typeof LANGUAGE_OPTIONS)[number];
export type GenderOption = (typeof GENDER_OPTIONS)[number];

export interface VoiceMatrixVoice {
  name: string;
  id: string;
  model: string;
}

/** Mentology core voice matrix — single source of truth for all setup/enrolment screens. */
export const VOICE_MATRIX: Record<LanguageOption, Record<GenderOption, VoiceMatrixVoice>> = {
  'English (Global)': {
    Male: { name: 'Tony', id: 'v9I7auPeR1xGKYRPwQGG', model: ELEVENLABS_TTS_MODEL },
    Female: { name: 'Lorina', id: 'l006hw6wZaEYAv80cbzj', model: ELEVENLABS_TTS_MODEL },
  },
  'English (Aust)': {
    Male: { name: 'Matt', id: '8jPqFrVhlKDhUemqIdfi', model: ELEVENLABS_TTS_MODEL },
    Female: { name: 'Jill', id: 's116Gqp9aiAQAe29Albd', model: ELEVENLABS_TTS_MODEL },
  },
  Spanish: {
    Male: { name: 'Dani', id: 'CdAqYBLnsNjmTqYgD5Ha', model: ELEVENLABS_TTS_MODEL },
    Female: { name: 'Norah', id: 'kcQkGnn0HAT2JRDQ4Ljp', model: ELEVENLABS_TTS_MODEL },
  },
  Afrikaans: {
    Male: { name: 'Dave', id: 'jBROPXpvLOBdHchUg1oS', model: ELEVENLABS_TTS_MODEL },
    Female: { name: 'Laura', id: '1LzJNEgXijcCsx4WZvpL', model: ELEVENLABS_TTS_MODEL },
  },
};

export interface VoiceEntry {
  language: LanguageOption;
  gender: GenderOption;
  name: string;
  voiceId: string;
  modelId: string;
}

const REGION_LABELS: Record<LanguageOption, string> = {
  'English (Aust)': 'Australian',
  'English (Global)': 'Global',
  Spanish: 'Spanish',
  Afrikaans: 'Afrikaans',
};

function matrixVoiceToEntry(language: LanguageOption, gender: GenderOption): VoiceEntry {
  const voice = VOICE_MATRIX[language][gender];
  return {
    language,
    gender,
    name: voice.name,
    voiceId: voice.id,
    modelId: voice.model,
  };
}

/** Flat registry derived from VOICE_MATRIX for legacy callers and server parity. */
export const VOICE_REGISTRY: VoiceEntry[] = LANGUAGE_OPTIONS.flatMap((language) =>
  GENDER_OPTIONS.map((gender) => matrixVoiceToEntry(language, gender)),
);

export function resolveVoiceFromMatrix(languageTarget: string, aiMentorGender: string): VoiceEntry | null {
  if (!languageTarget || !aiMentorGender) return null;
  const language = languageTarget as LanguageOption;
  const gender = aiMentorGender as GenderOption;
  if (!VOICE_MATRIX[language]?.[gender]) return null;
  return matrixVoiceToEntry(language, gender);
}

export function resolveVoiceEntry(
  languageTarget: string,
  aiMentorGender: string,
  _registry: VoiceEntry[] = VOICE_REGISTRY,
): VoiceEntry | null {
  return resolveVoiceFromMatrix(languageTarget, aiMentorGender);
}

export function resolveVoiceProfile(languageTarget: string, aiMentorGender: string) {
  return resolveVoiceFromMatrix(languageTarget, aiMentorGender);
}

export function resolveModelForVoiceId(_voiceId: string, _registry: VoiceEntry[] = VOICE_REGISTRY): string {
  return ELEVENLABS_TTS_MODEL;
}

export function isNonEnglishLanguageTarget(languageTarget?: string | null) {
  return languageTarget === 'Spanish' || languageTarget === 'Afrikaans';
}

export function formatVoiceProfileLabel(entry: VoiceEntry): string {
  const region = REGION_LABELS[entry.language] ?? entry.language;
  return `${entry.name} (${region} ${entry.gender})`;
}

export function formatVoiceProfileLabelFromSelection(languageTarget: string, aiMentorGender: string): string | null {
  const entry = resolveVoiceFromMatrix(languageTarget, aiMentorGender);
  return entry ? formatVoiceProfileLabel(entry) : null;
}

export function migrateLegacyVoiceIdentity(name: string, voiceId: string) {
  let resolvedName = name;
  let resolvedVoiceId = voiceId;

  if (resolvedName === 'Sarah') resolvedName = 'Jill';
  if (resolvedName === 'Harry' || resolvedVoiceId === LEGACY_HARRY_VOICE_ID) {
    resolvedName = 'Tony';
    resolvedVoiceId = TONY_VOICE_ID;
  }

  return { name: resolvedName, voiceId: resolvedVoiceId };
}

export function extractMentorFirstName(languageTarget: string, aiMentorGender: string) {
  return resolveVoiceFromMatrix(languageTarget, aiMentorGender)?.name ?? 'Andre';
}

export function buildVoicePreviewText(mentorName: string, languageTarget?: string | null) {
  switch (languageTarget) {
    case 'Spanish':
      return `Hola, soy ${mentorName}, tu mentor de inteligencia artificial de Mentology.`;
    case 'Afrikaans':
      return `Hallo, ek is ${mentorName}, jou Mentology KI-mentor.`;
    default:
      return `Hello, I'm ${mentorName}, your Mentology AI-Study Partner.`;
  }
}

export function resolveMentorFromBio(bio: {
  language_target?: string;
  ai_mentor_gender?: string;
  ai_mentor_name?: string;
  ai_mentor_voice_id?: string;
} | null) {
  if (bio?.ai_mentor_name?.trim() && bio?.ai_mentor_voice_id?.trim()) {
    const migrated = migrateLegacyVoiceIdentity(
      bio.ai_mentor_name.trim(),
      bio.ai_mentor_voice_id.trim(),
    );
    return {
      name: migrated.name,
      voiceId: migrated.voiceId,
      modelId: resolveModelForVoiceId(migrated.voiceId),
    };
  }

  const entry = resolveVoiceFromMatrix(bio?.language_target || '', bio?.ai_mentor_gender || '');
  if (entry) {
    return { name: entry.name, voiceId: entry.voiceId, modelId: entry.modelId };
  }

  const defaultEntry = resolveVoiceFromMatrix('English (Aust)', 'Female');
  return {
    name: defaultEntry?.name ?? 'Jill',
    voiceId: defaultEntry?.voiceId ?? 's116Gqp9aiAQAe29Albd',
    modelId: defaultEntry?.modelId ?? ELEVENLABS_TTS_MODEL,
  };
}

/** MPL-10 classroom intro — voice profile selected on Screen-MPL-09 (language + gender matrix). */
export function resolveStudyPartnerFromBio(bio: {
  language_target?: string;
  ai_mentor_gender?: string;
  ai_mentor_name?: string;
  ai_mentor_voice_id?: string;
} | null) {
  const matrixEntry = resolveVoiceFromMatrix(
    bio?.language_target || '',
    bio?.ai_mentor_gender || '',
  );
  if (matrixEntry) {
    return {
      name: matrixEntry.name,
      voiceId: matrixEntry.voiceId,
      modelId: matrixEntry.modelId,
    };
  }

  if (bio?.ai_mentor_name?.trim() && bio?.ai_mentor_voice_id?.trim()) {
    const migrated = migrateLegacyVoiceIdentity(
      bio.ai_mentor_name.trim(),
      bio.ai_mentor_voice_id.trim(),
    );
    return {
      name: migrated.name,
      voiceId: migrated.voiceId,
      modelId: resolveModelForVoiceId(migrated.voiceId),
    };
  }

  const defaultEntry = resolveVoiceFromMatrix('English (Aust)', 'Female');
  return {
    name: defaultEntry?.name ?? 'Jill',
    voiceId: defaultEntry?.voiceId ?? 's116Gqp9aiAQAe29Albd',
    modelId: defaultEntry?.modelId ?? ELEVENLABS_TTS_MODEL,
  };
}

let activePreviewAudio: HTMLAudioElement | null = null;

export async function previewVoiceSample(
  voiceId: string,
  mentorName: string,
  options: { language?: string; gender?: string; modelId?: string } = {},
) {
  if (typeof window === 'undefined') return;

  cancelVoicePreview();
  cancelSpeechPlayback();

  const modelId = ELEVENLABS_TTS_MODEL;
  const previewText = buildVoicePreviewText(mentorName, options.language);
  const audio = await previewVoiceAudio(
    voiceId,
    previewText,
    { ...options, modelId, languageTarget: options.language },
  );
  if (audio) activePreviewAudio = audio;
}

export function cancelVoicePreview() {
  if (activePreviewAudio) {
    activePreviewAudio.pause();
    activePreviewAudio.currentTime = 0;
    activePreviewAudio.muted = true;
    activePreviewAudio.src = '';
    activePreviewAudio = null;
  }
  cancelElevenLabsPlayback();
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
}
